import {
    IDialogService,
    Dialog,
    DialogContentType,
    DialogSize,
    DialogDimension,
} from '@metaos/hub-sdk';

const DEFAULT_DIALOG_SIZE = DialogDimension.Medium;

export type PromiseHandlers = {
    onSubmit: (result?: string | object) => void;
    onDismiss: () => void;
};

export type DialogActions = {
    open: () => void;
    close: () => void;
    update: (dialog: Partial<Dialog>) => void;
};

export default class DialogService implements IDialogService {
    private actions: DialogActions;
    private promises: PromiseHandlers;

    constructor(actions: DialogActions, promises: PromiseHandlers) {
        this.actions = actions;
        this.promises = promises;
    }

    _sanitizeSize(size: DialogSize): DialogSize {
        const isValidType = (val: any) => typeof val === 'string' || typeof val === 'number';

        const sizeSanitized = {
            width: isValidType(size.width) ? size.width : DEFAULT_DIALOG_SIZE,
            height: isValidType(size.height) ? size.height : DEFAULT_DIALOG_SIZE,
        };

        // try to find it inside DialogDimension
        const validConstants = Object.values(DialogDimension);
        for (let [dim, value] of Object.entries(sizeSanitized)) {
            if (validConstants.indexOf(value) > -1) {
                // dimension has known constant value
                continue;
            }
            // dimension has unknown constant value
            // try to parse it as float
            const dimNum = parseFloat(value);
            if (isNaN(dimNum)) {
                // it's an in valid number
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (54,17): Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ width: number | DialogDimension | undefined; height: number | DialogDimension | undefined; }'.
                // @ts-expect-error
                sizeSanitized[dim] = DEFAULT_DIALOG_SIZE;
                continue;
            }
            // it's a valid number
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS7053 (61,13): Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ width: number | DialogDimension | undefined; height: number | DialogDimension | undefined; }'.
            // @ts-expect-error
            sizeSanitized[dim] = dimNum;
        }
        return sizeSanitized;
    }

    public open(dialog: Dialog): Promise<string | object> {
        if (
            dialog.type === DialogContentType.Card ||
            !dialog.hasOwnProperty('url') ||
            !dialog.url
        ) {
            // Fail silently
            return Promise.resolve('Unsupported dialog type');
        }

        const promise = new Promise<string | object>((res, rej) => {
            this.promises.onSubmit = (result: string | object | undefined) => {
                if (result) {
                    res(result);
                }
            };
            this.promises.onDismiss = () => {
                this.actions.close();
                rej();
            };
        });

        this.actions.update({
            ...dialog,
            ...this._sanitizeSize(dialog),
        });
        this.actions.open();

        return promise;
    }

    public resize(size: DialogSize): Promise<void> {
        this.actions.update(this._sanitizeSize(size));
        return Promise.resolve();
    }

    public submit(result?: string | object, _appIds?: string[]): Promise<void> {
        if (result) {
            this.promises.onSubmit(result);
            this.actions.close();
        }
        return Promise.resolve();
    }
}
